// Confounder generation for the engagement gate — orthographic near-misses of
// a word, used as distractors. Ported from lekterion's
// `src/lib/model/confounders.ts` (the pre-ante-twix char-colocation
// heuristic), with ONE deliberate deviation: the colocation tables are built
// at runtime from the original text already in memory (memoized per
// `DocumentModel`), not shipped per-language by an ingest step — retells'
// corpus is content-hashed and its tokenizer emits no surface strings, so the
// ingest pipeline is out of scope. The scoring itself is ported faithfully
// (`ante[L][x] + pre[R][x] + 2·twix[LR][x]`, single code-point substitution,
// same length) so corpus-wide tables can be swapped in later without touching
// consumers.
//
// Alphabet-agnostic throughout: the tables read whatever code points the text
// contains, so polytonic Greek and German umlauts/ß work without any `[a-z]`
// assumption. Pure module (functional-programming policy) apart from the
// explicit WeakMap memo at the bottom.

import { foldQuote } from "@bindery/convex/model/wordGloss";

/** Char-colocation frequency tables — the "pre-ante-twix" dict. */
export interface ColocationTables {
  /** `pre[c]` = predecessors of `c` → weighted count. */
  pre: Record<string, Record<string, number>>;
  /** `ante[c]` = successors of `c` → weighted count. */
  ante: Record<string, Record<string, number>>;
  /** `twix[c1c2]` = chars seen between the pair `c1`,`c2` → weighted count. */
  twix: Record<string, Record<string, number>>;
}

/** Code points of a string (surrogate-safe, unlike UTF-16 indexing). */
function cps(s: string): string[] {
  return [...s];
}

/**
 * Normalize a surface to its dict form: NFC + casefold — `foldQuote`, under
 * the name this module's consumers use. NOT a second implementation: the
 * gate's logged `word`/`chosen` fields and the `aiCache.quoteLower` consensus
 * key are compared against each other, so a copy that drifted by one
 * `.normalize` would desync the two silently.
 */
export { foldQuote as normalizeWord } from "@bindery/convex/model/wordGloss";

function bump(
  table: Record<string, Record<string, number>>,
  key: string,
  sub: string,
  by: number,
): void {
  const row = (table[key] ??= {});
  row[sub] = (row[sub] ?? 0) + by;
}

/**
 * Build the colocation tables from a word→frequency map (normalized words
 * with occurrence counts, so common spellings dominate the statistics). Pure
 * and deterministic.
 */
export function buildColocation(freq: Map<string, number>): ColocationTables {
  const pre: ColocationTables["pre"] = {};
  const ante: ColocationTables["ante"] = {};
  const twix: ColocationTables["twix"] = {};

  for (const [word, count] of freq) {
    const w = cps(word);
    // Each loop's bound is what makes every subscript in it in-range, so the
    // `| undefined` noUncheckedIndexedAccess adds is unreachable here.
    const at = (k: number): string => w[k]!;
    for (let i = 0; i + 1 < w.length; i++) {
      const a = at(i);
      const b = at(i + 1);
      bump(ante, a, b, count); // b follows a
      bump(pre, b, a, count); // a precedes b
    }
    for (let i = 0; i + 2 < w.length; i++) {
      bump(twix, at(i) + at(i + 2), at(i + 1), count); // middle sits betwixt
    }
  }

  return { pre, ante, twix };
}

/**
 * The word-frequency map of a document: every token's surface, normalized,
 * counted. The tokenizer stores only `[start, end)` offsets, so surfaces are
 * sliced from the immutable text here.
 */
export function wordFrequencies(model: {
  text: string;
  tokens: readonly { start: number; end: number }[];
}): Map<string, number> {
  const freq = new Map<string, number>();
  for (const t of model.tokens) {
    const word = foldQuote(model.text.slice(t.start, t.end));
    if (word === "") continue;
    freq.set(word, (freq.get(word) ?? 0) + 1);
  }
  return freq;
}

/**
 * Ranked confounders for one (already normalized) word: each is a single
 * code-point substitution, same length, scored by the colocation sum, never
 * the word itself. Deterministic: score desc, then code-point order as a
 * stable tiebreak. Empty/1-char words and words with no plausible
 * substitution return `[]` — the gate falls open for them (lekterion's own
 * documented behavior).
 */
export function getOptions(
  word: string,
  tables: ColocationTables,
  limit = 12,
): string[] {
  const w = cps(word);
  const scored = new Map<string, number>();

  for (let p = 0; p < w.length; p++) {
    const left = p > 0 ? (w[p - 1] ?? null) : null;
    const right = p + 1 < w.length ? (w[p + 1] ?? null) : null;
    // Candidate replacements: the union of what the three tables have seen in
    // this neighbourhood — bounds us to corpus-attested letters.
    const candidates = new Set<string>();
    if (left !== null)
      for (const k of Object.keys(tables.ante[left] ?? {})) candidates.add(k);
    if (right !== null)
      for (const k of Object.keys(tables.pre[right] ?? {})) candidates.add(k);
    if (left !== null && right !== null)
      for (const k of Object.keys(tables.twix[left + right] ?? {}))
        candidates.add(k);

    for (const char of candidates) {
      if (char === w[p]) continue;
      const form = w.slice(0, p).join("") + char + w.slice(p + 1).join("");
      if (form === word) continue;
      // The pre-ante-twix colocation sum; the two-sided twix term weighs
      // double (lekterion's `colocationScorer`, verbatim).
      let score = 0;
      if (left !== null) score += tables.ante[left]?.[char] ?? 0;
      if (right !== null) score += tables.pre[right]?.[char] ?? 0;
      if (left !== null && right !== null)
        score += 2 * (tables.twix[left + right]?.[char] ?? 0);
      if (score <= 0) continue;
      const prev = scored.get(form);
      if (prev === undefined || score > prev) scored.set(form, score);
    }
  }

  return [...scored.entries()]
    .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))
    .slice(0, limit)
    .map(([form]) => form);
}

/** ASCII quotes and the typographic form `foldQuote` folded them from. */
const CURLY_FOR_ASCII: Record<string, string> = { "'": "’", '"': "”" };

/**
 * Re-apply the source word's per-position surface onto a normalized confounder
 * so the distractor reads like the selected word — else the odd chip gives the
 * answer away. Two things are restored, for the same reason:
 *
 * - CASE, so the one capitalized chip is not the answer;
 * - the QUOTE GLYPH, so the one chip with a typographic apostrophe is not
 *   either. The reader's text is educated (`curlQuotes`) while `normalizeWord`
 *   folds to ASCII, so `hält’s` would otherwise stand beside `halt's`.
 *
 * No-op when the code-point lengths differ (rare casefold edge, e.g. ß→SS).
 */
export function applySurfacePattern(template: string, lower: string): string {
  const t = cps(template);
  const l = cps(lower);
  if (t.length !== l.length) return lower;
  return l
    .map((ch, i) => {
      // Only where the confounder ALREADY has a quote — never turn one of its
      // own letters into a mark it does not have.
      const curly = CURLY_FOR_ASCII[ch];
      if (curly !== undefined && t[i] === curly) return curly;
      return t[i] !== t[i]?.toLowerCase() ? ch.toUpperCase() : ch;
    })
    .join("");
}

/**
 * Per-document memo of the colocation tables (built once per chapter model,
 * shared by every gate opened in it). A WeakMap on the model object — chapter
 * models are immutable and stable for the life of a reading view, and the
 * entry falls away with the model.
 */
const tablesByModel = new WeakMap<object, ColocationTables>();

export function colocationForModel(model: {
  text: string;
  tokens: readonly { start: number; end: number }[];
}): ColocationTables {
  const cached = tablesByModel.get(model);
  if (cached) return cached;
  const tables = buildColocation(wordFrequencies(model));
  tablesByModel.set(model, tables);
  return tables;
}
